import { CompileShallowModuleMetadata, ThrowStmt } from '@angular/compiler';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { DxChartComponent } from 'devextreme-angular';
import { UnitsService } from 'pumpcalc-core';
import { PlotConfig } from '../chart-options/chart-options.component';

export const superScriptNumbers = [0x2070, 0x00B9, 0x00B2, 0x00B3, 0x2074, 0x2075, 0x2076, 0x2077, 0x2078, 0x2079];
export const superScriptMinus = 0x207B;


@Component({
  selector: 'app-ranged-result-chart',
  templateUrl: './ranged-result-chart.component.html',
  styleUrls: ['./ranged-result-chart.component.css']
})
export class RangedResultChartComponent implements OnInit {

  @ViewChildren('chart') charts:QueryList<DxChartComponent>;
  pinnedSelection=false;
  dataInSi:any[]=[];
  dataInSelectedUnits:any[]=[];
  argTitle;
  valTitle;
  showConfig=[false,false];
  plotConfig=[new PlotConfig(),new PlotConfig()]
  visualRangeX=[[this.plotConfig[0].minX,this.plotConfig[0].maxX],[this.plotConfig[1].minX,this.plotConfig[1].maxX]];
  visualRangeY=[[this.plotConfig[0].minY,this.plotConfig[0].maxY],[this.plotConfig[1].minY,this.plotConfig[1].maxY]];
  lastPinnedSelectionQuantity;
  lastSeriesSelectionQuantity;

  constructor(public unitsService:UnitsService) {
    this.unitsService.unitChangeAnnounced.subscribe((change=>{
      this.convertDataToSelectedUnit();
    }))
  }

  setSeriesSelection(seriesSelection){
    this.dataInSi = this.dataInSi.length<=1?[this.getData(this.clone(seriesSelection))]:[this.dataInSi[0],this.getData(this.clone(seriesSelection))];
    this.convertDataToSelectedUnit();
    if(this.lastSeriesSelectionQuantity != seriesSelection.plots[0].valQuantity) {
      this.setPlotConfig(new PlotConfig(),this.dataInSi.length<=1?0:1,seriesSelection.plots[0].logX);
    } else {
      this.updateDatePlotConfigForSeriesSelection(seriesSelection,this.dataInSi.length<=1?0:1);
    }
    this.lastSeriesSelectionQuantity = seriesSelection.plots[0].valQuantity;
  }

  setPinnedSelection(pinnedSelection){
    this.dataInSi = [this.getData(this.clone(pinnedSelection)),this.clone(this.dataInSi[this.dataInSi.length-1])];
    this.convertDataToSelectedUnit();
    this.pinnedSelection=true;
    this.setPlotConfig(this.clone(this.plotConfig[0]),0,pinnedSelection.plots[0].logX);
    this.setPlotConfig(this.clone(this.plotConfig[0]),1,pinnedSelection.plots[0].logX);
  }

  clearPinnedSelection(){
    this.dataInSi = [this.clone(this.dataInSi[this.dataInSi.length-1])];
    this.convertDataToSelectedUnit();
    this.pinnedSelection=false;
    this.setPlotConfig(this.clone(this.plotConfig[1]),0,this.plotConfig[1].logX);
  }

  getData(fromSelection:any){
    let argTitle = fromSelection.plots[0]?fromSelection.plots[0].argTitle:"";
    let valTitle = fromSelection.plots[0]?fromSelection.plots[0].valTitle:"";
    let argQuantity = fromSelection.plots[0]?fromSelection.plots[0].argQuantity:"";
    let valQuantity = fromSelection.plots[0]?fromSelection.plots[0].valQuantity:"";
    let flattenedSeries = this.clone(fromSelection);
    flattenedSeries.series.forEach(series => {
      series.pipeReports.forEach(function(report,index){
        Object.keys(report).forEach(key=>{
          series[(index+1)+key] = report[key];
        })
      });
    });
    return {series:flattenedSeries.series,plots:fromSelection.plots,valTitle:valTitle,argTitle:argTitle,argQuantity:argQuantity,valQuantity:valQuantity};
  }

  ngOnInit() {
  }

  clone(obj:any):any{
    return JSON.parse(JSON.stringify(obj));
  }

  convertDataToSelectedUnit(){
    this.dataInSelectedUnits = this.clone(this.dataInSi);
    this.dataInSelectedUnits.forEach(chartData=>{
      let convertedKeys={};
      chartData.plots.forEach(plot => {
         if(plot.argQuantity){
          chartData.series.forEach(point => {
            point[plot.arg] = !convertedKeys[plot.arg]?this.unitsService.convertFromSIForQuantity(plot.argQuantity,point[plot.arg]):point[plot.arg];
          });
          convertedKeys[plot.arg]=true;
        }
        if(plot.valQuantity){
          chartData.series.forEach(point => {
            point[plot.val] = !convertedKeys[plot.val]?this.unitsService.convertFromSIForQuantity(plot.valQuantity,point[plot.val]):point[plot.val];
          });
          convertedKeys[plot.val]=true;
        }
      });
    })
  }

  plotConfigChange(plotConfig,index){
    this.setPlotConfig(plotConfig,index,plotConfig.logX);
  }

  setPlotConfig(plotConfig,index,logX){
    this.plotConfig[index]=plotConfig;
    this.plotConfig[index].logX=logX;
    this.visualRangeX=[[this.plotConfig[0].minX,this.plotConfig[0].maxX],[this.plotConfig[1].minX,this.plotConfig[1].maxX]];
    this.visualRangeY=[[this.plotConfig[0].minY,this.plotConfig[0].maxY],[this.plotConfig[1].minY,this.plotConfig[1].maxY]];
  }

  resetZoom(index){
    if(index==0) {
      this.setPlotConfig(this.clone(this.plotConfig[0]),0,this.plotConfig[0].logX);
    }
    else {
      this.setPlotConfig(this.clone(this.plotConfig[1]),1,this.plotConfig[1].logX);
    }
  }

  export(index){
    if(index==0){this.charts.first.instance.exportTo("chart", "png");}
    else {this.charts.last.instance.exportTo("chart", "png");}
  }

  customizeText(arg) {
    let val = arg.valueText;
    // check if it is scientific notation
    if (val.indexOf("E") == -1) return arg.valueText;
    // get exponent value
    let pow = val.substr(val.indexOf("E") + 2, val.length - 1);
    let powAsString = "";
    for(let i = 0; i <= pow.length -1; i++){
      powAsString += String.fromCharCode(superScriptNumbers[parseInt(pow.charAt(i))]);
    }
    // get lead value if any
    let n = val.substr(0, val.indexOf("E"));
    n = parseFloat(n) == 1.0 ? 10 : n + "*10";
    return n + (val.indexOf("-") != -1 ? String.fromCharCode(superScriptMinus) : "") + powAsString;
  }

  updateDatePlotConfigForSeriesSelection(seriesSelection,index){
    this.plotConfig[index].logX=seriesSelection.plots[0].logX;
  }

}

